import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/utils/cn'

/**
 * Touch-only screens never show hover tooltips (a tap doesn't open them), so
 * skip the Radix tree there — list rows/cards render one per action button.
 */
const touchOnly =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(hover: none)').matches
    : false

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip: React.FC<TooltipPrimitive.TooltipProps> = touchOnly
  ? ({ children }) => <>{children}</>
  : TooltipPrimitive.Root

const TouchTrigger = React.forwardRef<HTMLButtonElement, TooltipPrimitive.TooltipTriggerProps>(
  ({ asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp ref={ref} {...props} />
  },
)
TouchTrigger.displayName = 'TooltipTrigger'

const TooltipTrigger = touchOnly ? TouchTrigger : TooltipPrimitive.Trigger

const PointerTooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[60] max-w-xs rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground shadow-elevated',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
PointerTooltipContent.displayName = TooltipPrimitive.Content.displayName

const TooltipContent = touchOnly ? () => null : PointerTooltipContent

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
